import { useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Driver } from 'driver.js'
import { RootState } from 'store/modules/rootReducer'
import { markTimerCardTourSeen } from 'store/modules/tour'
import { getTimerCardTourConfig, createDriverInstance } from 'pages/Planning/utils/tourConfig'
import { trackTimerCardTourShown } from 'pages/Planning/utils/tourTracking'
import { useTracking } from 'react-tracking'

interface UseTimerCardTourProps {
  isDarkMode: boolean
}

export const useTimerCardTour = ({ isDarkMode }: UseTimerCardTourProps) => {
  const dispatch = useDispatch()
  const { trackEvent } = useTracking()
  const driverRef = useRef<Driver | null>(null)
  const tourState = useSelector((state: RootState) => state.tour.dashboard)

  // Check if element exists
  const elementExists = useCallback((selector: string): boolean => {
    try {
      const element = document.querySelector(selector)
      return element !== null && element instanceof HTMLElement
    } catch {
      return false
    }
  }, [])

  // Start timer card tour
  const startTour = useCallback(() => {
    // Don't show if already seen
    if (tourState.timerCardTourSeen) return

    // Check if timer card exists
    if (!elementExists('[data-tour="timer-card-container"]')) return

    // Get tour config
    const config = getTimerCardTourConfig(isDarkMode)

    // Verify first element exists
    if (!elementExists(config[0].element)) return

    // Track tour shown
    trackEvent(trackTimerCardTourShown())

    // Create driver instance with onDestroyed callback
    const handleTourComplete = () => {
      // Mark as seen when tour completes
      dispatch(markTimerCardTourSeen())
    }

    const driverInstance = createDriverInstance(isDarkMode, handleTourComplete)

    // Configure driver with steps
    driverInstance.setSteps(
      config.map((stepConfig) => ({
        element: stepConfig.element,
        popover: {
          title: stepConfig.popover.title,
          description: stepConfig.popover.description,
          side: stepConfig.popover.side || 'top',
          align: stepConfig.popover.align || 'center',
        },
      }))
    )

    // Store the instance
    driverRef.current = driverInstance

    // Start tour with a small delay to ensure DOM is ready
    setTimeout(() => {
      if (driverRef.current) {
        try {
          driverRef.current.drive()
        } catch (error) {
          console.error('Error starting timer card tour:', error)
          // Mark as seen on error to prevent retry loops
          dispatch(markTimerCardTourSeen())
        }
      }
    }, 500)
  }, [isDarkMode, tourState.timerCardTourSeen, dispatch, elementExists, trackEvent])

  // Auto-start tour when card appears and hasn't been seen
  useEffect(() => {
    if (!tourState.timerCardTourSeen && elementExists('[data-tour="timer-card-container"]')) {
      const timer = setTimeout(() => {
        startTour()
      }, 1000) // Delay to ensure everything is loaded

      return () => clearTimeout(timer)
    }
  }, [tourState.timerCardTourSeen, startTour, elementExists])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
    }
  }, [])

  return {
    startTour,
  }
}
